<template>
  <profile-modal-base
    data-test="profile-show-modal"
    :is-open="showModal"
    @close="close"
  >
    <template #header>
      <profile-modal-header
        data-test="modal-header"
        :header-text="'Profile'"
      ></profile-modal-header>
    </template>
    <div class="modal-content">
      <div class="flexbox-row">
        <div class="avatar-container" data-test="avatar-container">
          <img
            v-if="avatarThumbUrl"
            :src="avatarThumbUrl"
            alt="avatar"
            class="avatar"
          />
          <span v-else-if="firstInitial" class="display-xs font-semibold first-initial">
            {{ firstInitial }}
          </span>
          <span v-else>
            <icon name="avatar" />
          </span>
        </div>
        <div class="flexbox-row spacer flex-start">
          <div>
            <div data-test="first-name" class="text-lg font-semibold text-primary">
              {{ firstName }}
            </div>
            <div
              v-if="testDate"
              class="text-sm"
              data-test="test-date"
            >
              Test Date: {{ testDate }}
            </div>
          </div>
          <div class="spacer"></div>
          <div class="edit-profile-button">
            <button
              data-test="edit-profile-button"
              class="button-reset button-link text-sm font-semibold"
              @click="open('editProfileModal')"
            >
              Edit Profile
            </button>
          </div>
        </div>
      </div>
      <div
        v-if="showBookmarksDiscussions"
        class="flexbox-row button-row"
      >
        <button
          data-test="bookmark-button"
          class="profile-button"
          @click="goto('bookmarks#index')"
        >
          <span class="icon-xs">
            <icon name="bookmark" variant="326ff0" />
          </span>
          <span class="text-sm font-semibold">Bookmarks</span>
        </button>
        <button
          v-if="isDiscussionEnabled"
          data-test="discussions-button"
          class="profile-button"
          @click="goto('discussions#index')"
        >
          <div class="icon-xs">
            <icon name="speech-bubble" />
          </div>
          <span class="text-sm font-semibold">Discussions</span>
        </button>
      </div>

      <modal-row-group>
        <button
          v-if="showUpgrade"
          class="button-reset flexbox-row row-with-icon"
          data-test="upsell-button"
          @click="upsell"
        >
          <span class="icon-wrapper">
            <icon name="gift" class="icon-small"></icon>
          </span>
          <span class="text-md font-medium text-brand-primary">
            {{ trialEnabled ? 'Start your trial' : 'Subscribe' }}
          </span>
        </button>
        <button
          class="button-reset flexbox-row row-with-icon"
          data-test="account-button"
          @click="open('accountModal')"
        >
          <span class="icon-wrapper">
            <icon name="avatar" class="icon-small"></icon>
          </span>
          <span class="text-md font-medium">
            Account
          </span>
        </button>
      </modal-row-group>

      <modal-row-group>
        <button
          class="button-reset flexbox-row row-with-icon"
          data-test="faq-button"
          @click="gotoURL('https://hltcorp.zendesk.com/hc/en-us');"
        >
          <span class="icon-wrapper">
            <icon name="faq" class="icon-small"></icon>
          </span>
          <span class="text-md font-medium">
            FAQ
          </span>
        </button>
        <button
          class="button-reset flexbox-row row-with-icon"
          data-test="support-button"
          @click="open('helpAndSupportModal')"
        >
          <span class="icon-wrapper">
            <icon name="help-support" class="icon-small"></icon>
          </span>
          <span class="text-md font-medium">
            Help & Support
          </span>
        </button>
      </modal-row-group>

      <modal-row-group>
        <button
          v-if="resettableSims.length"
          class="button-reset flexbox-row row-with-icon"
          data-test="exam-simulations-button"
          @click="open('examSimulationsModal')"
        >
          <span class="icon-wrapper">
            <icon name="laptop" class="icon-small"></icon>
          </span>
          <span class="text-md font-medium">
            Exam Simulations
          </span>
        </button>
      </modal-row-group>

      <modal-row-group>
        <button
          v-if="showAdditionalSettings"
          class="button-reset flexbox-row row-with-icon"
          data-test="additional-settings-button"
          @click="open('additionalSettingsModal')"
        >
          <span class="icon-wrapper">
            <icon name="wrench" class="icon-small"></icon>
          </span>
          <span class="text-md font-medium">
            Additional Settings
          </span>
        </button>
      </modal-row-group>

      <modal-row-group>
        <button
          class="button-reset flexbox-row row-with-icon"
          data-test="sign-out-button"
          @click="signOut"
        >
          <span class="icon-wrapper">
            <icon name="log-out" variant="D92D20" class="icon-small"></icon>
          </span>
          <span class="text-md font-medium text-error">
            Log out
          </span>
        </button>
      </modal-row-group>
    </div>
  </profile-modal-base>
</template>

<script>
import Icon from '@/components/singletons/icon';
import AvatarMixin from '@/mixins/avatar';
import ProfileModalMixin from '@/mixins/profileModal';
import TrialMixin from '@/mixins/trial';
import ProfileModalBase from './ProfileModalBase.vue';
import ProfileModalHeader from './ProfileModalHeader.vue';
import ModalRowGroup from './ModalRowGroup.vue';

const MODAL_NAME = 'profileShowModal';

export default {
  name: 'ProfileShowModal',
  components: {
    ModalRowGroup,
    ProfileModalBase,
    ProfileModalHeader,
    Icon,
  },
  mixins: [AvatarMixin, ProfileModalMixin, TrialMixin],
  computed: {
    isDiscussionEnabled() {
      return this.$store.getters['discussions/enabled'];
    },
    showModal() {
      return this.$store.getters['ui/profileModalShown'] === MODAL_NAME;
    },
    certApp() {
      return this.$store.getters['app/isCertApp'];
    },
  },
  methods: {
    close() {
      this.$store.commit('ui/setProfileModalShown', '');
    },
    goto(route) {
      this.close();
      this.$router.push({name: route});
    },
    gotoURL(url) {
      window.open(url, '_blank');
    },
    signOut() {
      this.$store.dispatch('session/signOut');
    },
  },
};
</script>

<style scoped lang="scss">
.avatar {
  border-radius: 50%;
}

.first-initial {
  color: var(--fg-quaternary);
}

.modal-content {
  padding-top: rem-calc(20);
  flex: 1;
  background-color: var(--bg-light);
}

.spacer {
  flex: 1;
}

.edit-profile-button {
  align-self: start;
}

.icon-wrapper {
  display: grid;
  place-items: center;
  width: rem-calc(20);
}

.icon-small {
  height: 16px;
}

.icon-xs {
  max-height: 15px;
}

.profile-button {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: rem-calc(8);
  height: rem-calc(40);
  flex: 1;
  border-radius: var(--radius-md);
  background-color: white;
  box-shadow: 0px 1px 2px 0px rgba(10, 13, 18, 0.05);
  box-shadow: 0px -2px 0px 0px rgba(10, 13, 18, 0.05) inset;
  box-shadow: 0px 0px 0px 1px rgba(10, 13, 18, 0.18) inset;
}

.button-row {
  margin-top: 1.25rem;
  padding-bottom: 1.25rem;
  margin-bottom: 2rem;
  border-bottom: 1px solid var(--border-secondary);
}

.row-with-icon {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: rem-calc(8);
  padding: 0 rem-calc(20);
  height: rem-calc(48);
}

@include breakpoint(tablet) {
  .modal-content {
    background: transparent;
    padding-left: 0;
    padding-right: 0;
  }
}

@include breakpoint(desktop) {
  .modal-content {
    background: transparent;
    padding-left: 0;
    padding-right: 0;
  }
}
</style>
